import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import toast from 'react-hot-toast';

import { API_BASE_URL as BASE_URL } from './config';

class ApiService {
  private api: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: any) => void;
  }> = [];

  constructor() {
    this.api = axios.create({
      baseURL: BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          console.log('Adding auth header to request:', config.url, 'Token:', token.substring(0, 10) + '...');
        } else {
          console.log('No access token found for request:', config.url);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle 401/403 and refresh token
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        // Handle both 401 and 403 with token refresh attempt
        if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
          // Skip refresh attempt for auth endpoints to avoid infinite loops
          if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/register')) {
            return Promise.reject(error);
          }

          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.api(originalRequest);
            }).catch((refreshError) => {
              return Promise.reject(error);
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (!refreshToken) {
              console.log('No refresh token available, logging out');
              throw new Error('No refresh token');
            }

            console.log('Attempting to refresh token due to', error.response?.status, 'error');
            const response = await this.refreshToken(refreshToken);
            const { accessToken, refreshToken: newRefreshToken } = response.data;

            localStorage.setItem('accessToken', accessToken);
            localStorage.setItem('refreshToken', newRefreshToken);
            
            console.log('Token refreshed successfully');
            this.processQueue(null, accessToken);
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            
            return this.api(originalRequest);
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            this.processQueue(refreshError, null);
            
            // Show user-friendly message based on error type
            if (error.response?.status === 403) {
              toast.error('Session expired. Please login again.');
            } else {
              toast.error('Authentication failed. Please login again.');
            }
            
            this.logout();
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        // Handle other errors
        if (error.response?.data && typeof error.response.data === 'object' && 'message' in error.response.data) {
          toast.error((error.response.data as any).message);
        } else if (error.response?.status === 500) {
          toast.error('Server error. Please try again later.');
        } else if (error.code === 'NETWORK_ERROR' || !error.response) {
          toast.error('Network error. Please check your connection.');
        } else {
          toast.error('An unexpected error occurred');
        }

        return Promise.reject(error);
      }
    );
  }

  private processQueue(error: any, token: string | null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token!);
      }
    });

    this.failedQueue = [];
  }

  private async refreshToken(refreshToken: string) {
    try {
      const response = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
      return response;
    } catch (error) {
      console.error('Refresh token request failed:', error);
      throw error;
    }
  }

  private logout() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    window.location.href = '/login';
  }

  // Generic methods
  async get<T>(url: string, params?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.api.get(url, { params, ...config });
  }

  async post<T>(url: string, data?: any): Promise<AxiosResponse<T>> {
    return this.api.post(url, data);
  }

  async put<T>(url: string, data?: any): Promise<AxiosResponse<T>> {
    return this.api.put(url, data);
  }

  async patch<T>(url: string, data?: any): Promise<AxiosResponse<T>> {
    return this.api.patch(url, data);
  }

  async delete<T>(url: string): Promise<AxiosResponse<T>> {
    return this.api.delete(url);
  }
}

export const apiService = new ApiService();